import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Upload, Download, FileText, Eye, EyeOff } from 'lucide-react';
import { settingsAPI } from '../../services/apiService';

const DocumentsTab = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');

  const [newDocument, setNewDocument] = useState({
    name_de: '',
    name_ru: '',
    description_de: '',
    description_ru: '',
    filename_de: '',
    file_url_de: '',
    file_size_de: null,
    filename_ru: '',
    file_url_ru: '',
    file_size_ru: null,
    active: true,
    sort_order: 0
  });

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const response = await settingsAPI.getDocuments();
      setDocuments(response.data.documents || []);
    } catch (error) {
      console.error('Error loading documents:', error);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event, language) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type (only PDFs)
    if (file.type !== 'application/pdf') {
      setUploadStatus('Ошибка: Разрешены только PDF файлы');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setUploadStatus('Ошибка: Размер файла не должен превышать 10MB');
      return;
    }

    try {
      setUploadStatus(`Загрузка файла (${language === 'de' ? 'Немецкий' : 'Русский'})...`);
      
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${window.location.origin}/api/admin/uploads/document`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to upload file');
      }

      const uploadResult = await response.json();
      
      // Update the form with uploaded file info based on language
      if (editingDocument) {
        setEditingDocument({
          ...editingDocument,
          [`filename_${language}`]: uploadResult.filename,
          [`file_url_${language}`]: uploadResult.url,
          [`file_size_${language}`]: uploadResult.size
        });
      } else {
        setNewDocument({
          ...newDocument,
          [`filename_${language}`]: uploadResult.filename,
          [`file_url_${language}`]: uploadResult.url,
          [`file_size_${language}`]: uploadResult.size
        });
      }

      setUploadStatus(`Файл (${language === 'de' ? 'Немецкий' : 'Русский'}) успешно загружен`);
      setTimeout(() => setUploadStatus(''), 3000);
    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadStatus(`Ошибка при загрузке файла (${language === 'de' ? 'Немецкий' : 'Русский'})`);
      setTimeout(() => setUploadStatus(''), 3000);
    }
  };

  const resetForm = () => {
    setNewDocument({
      name_de: '',
      name_ru: '',
      description_de: '',
      description_ru: '',
      filename_de: '',
      file_url_de: '',
      file_size_de: null,
      filename_ru: '',
      file_url_ru: '',
      file_size_ru: null,
      active: true,
      sort_order: 0
    });
  };

  const handleSaveDocument = async () => {
    try {
      setSaving(true);
      
      console.log('Saving document:', editingDocument || newDocument);
      
      if (editingDocument) {
        // Update existing document
        console.log('Updating document with ID:', editingDocument.id);
        const response = await settingsAPI.updateDocument(editingDocument.id, editingDocument);
        console.log('Update response:', response);
      } else {
        // Create new document
        console.log('Creating new document:', newDocument);
        const response = await settingsAPI.createDocument(newDocument);
        console.log('Create response:', response);
      }

      await loadDocuments();
      setEditingDocument(null);
      setShowAddForm(false);
      resetForm();
      
      alert(editingDocument ? 'Документ успешно обновлен' : 'Документ успешно создан');
    } catch (error) {
      console.error('Error saving document:', error);
      console.error('Error details:', error.response?.data);
      alert(`Ошибка при сохранении документа: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот документ?')) {
      return;
    }

    try {
      console.log('Deleting document with ID:', documentId);
      const response = await settingsAPI.deleteDocument(documentId);
      console.log('Delete response:', response);
      await loadDocuments();
      alert('Документ успешно удален');
    } catch (error) {
      console.error('Error deleting document:', error);
      console.error('Error details:', error.response?.data);
      alert(`Ошибка при удалении документа: ${error.response?.data?.detail || error.message}`);
    }
  };

  const toggleDocumentActive = async (document) => {
    try {
      const updatedDocument = { ...document, active: !document.active };
      await settingsAPI.updateDocument(document.id, updatedDocument);
      await loadDocuments();
    } catch (error) {
      console.error('Error toggling document status:', error);
      alert('Ошибка при изменении статуса документа');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Неизвестно';
    const kb = bytes / 1024;
    const mb = kb / 1024;
    if (mb >= 1) {
      return `${mb.toFixed(1)} MB`;
    }
    return `${Math.round(kb)} KB`;
  };

  if (loading) {
    return <div className="text-center py-8">Загрузка документов...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-semibold text-white">Управление документами</h3>
        <button
          onClick={() => setShowAddForm(true)}
          className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
        >
          <Plus className="h-4 w-4 mr-2" />
          Добавить документ
        </button>
      </div>

      {uploadStatus && (
        <div className={`p-3 rounded-md ${uploadStatus.includes('Ошибка') 
          ? 'bg-red-900 text-red-200' 
          : uploadStatus.includes('успешно') 
            ? 'bg-green-900 text-green-200'
            : 'bg-blue-900 text-blue-200'
        }`}>
          {uploadStatus}
        </div>
      )}

      {/* Documents List */}
      <div className="space-y-4">
        {documents.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Документы не найдены</p>
            <p className="text-sm">Добавьте первый документ для скачивания</p>
          </div>
        ) : (
          documents
            .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
            .map((document) => (
              <div key={document.id} className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <FileText className="h-5 w-5 text-blue-400 mr-2" />
                      <h4 className="text-lg font-semibold text-white">
                        {document.name_de} / {document.name_ru}
                      </h4>
                      <button
                        onClick={() => toggleDocumentActive(document)}
                        className={`ml-3 p-1 rounded ${
                          document.active 
                            ? 'text-green-400 hover:bg-green-900' 
                            : 'text-gray-500 hover:bg-gray-700'
                        }`}
                        title={document.active ? 'Скрыть документ' : 'Показать документ'}
                      >
                        {document.active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                      </button>
                    </div>
                    
                    {(document.description_de || document.description_ru) && (
                      <div className="text-sm text-gray-300 mb-2">
                        <p><strong>DE:</strong> {document.description_de}</p>
                        <p><strong>RU:</strong> {document.description_ru}</p>
                      </div>
                    )}
                    
                    <div className="flex items-center flex-wrap gap-4 text-sm text-gray-400">
                      {/* Files info */}
                      {document.filename_de && (
                        <span>📄 DE: {document.filename_de}</span>
                      )}
                      {document.filename_ru && (
                        <span>📄 RU: {document.filename_ru}</span>
                      )}
                      {!document.filename_de && !document.filename_ru && document.filename && (
                        <span>📄 Legacy: {document.filename}</span>
                      )}
                      
                      {/* File sizes */}
                      {document.file_size_de && (
                        <span>📊 DE: {formatFileSize(document.file_size_de)}</span>
                      )}
                      {document.file_size_ru && (
                        <span>📊 RU: {formatFileSize(document.file_size_ru)}</span>
                      )}
                      {!document.file_size_de && !document.file_size_ru && document.file_size && (
                        <span>📊 Legacy: {formatFileSize(document.file_size)}</span>
                      )}
                      
                      <span>🔢 Порядок: {document.sort_order || 0}</span>
                      
                      {/* Download links */}
                      {document.file_url_de && (
                        <a
                          href={`${window.location.origin}${document.file_url_de}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center text-blue-400 hover:text-blue-300"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          DE
                        </a>
                      )}
                      {document.file_url_ru && (
                        <a
                          href={`${window.location.origin}${document.file_url_ru}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center text-green-400 hover:text-green-300"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          RU
                        </a>
                      )}
                      {!document.file_url_de && !document.file_url_ru && document.file_url && (
                        <a
                          href={`${window.location.origin}${document.file_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center text-gray-400 hover:text-gray-300"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Legacy
                        </a>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => setEditingDocument(document)}
                      className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded"
                      title="Редактировать"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteDocument(document.id)}
                      className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900 rounded"
                      title="Удалить"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
        )}
      </div>

      {/* Add/Edit Form */}
      {(showAddForm || editingDocument) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg max-w-2xl w-full mx-4 max-h-screen overflow-y-auto">
            <h3 className="text-xl font-semibold text-white mb-4">
              {editingDocument ? 'Редактировать документ' : 'Добавить документ'}
            </h3>
            
            <div className="space-y-4">
              {/* File Upload - German */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  PDF файл (Немецкий)
                </label>
                <div className="flex items-center space-x-4">
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => handleFileUpload(e, 'de')}
                    className="block w-full text-sm text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700"
                  />
                  {(editingDocument?.filename_de || newDocument.filename_de) && (
                    <span className="text-sm text-green-400">
                      ✓ {editingDocument?.filename_de || newDocument.filename_de}
                    </span>
                  )}
                </div>
              </div>

              {/* File Upload - Russian */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  PDF файл (Русский)
                </label>
                <div className="flex items-center space-x-4">
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => handleFileUpload(e, 'ru')}
                    className="block w-full text-sm text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-green-600 file:text-white hover:file:bg-green-700"
                  />
                  {(editingDocument?.filename_ru || newDocument.filename_ru) && (
                    <span className="text-sm text-green-400">
                      ✓ {editingDocument?.filename_ru || newDocument.filename_ru}
                    </span>
                  )}
                </div>
              </div>

              {/* Info message */}
              <div className="bg-blue-900 bg-opacity-30 border border-blue-700 rounded-lg p-3">
                <p className="text-sm text-blue-200">
                  <strong>Информация:</strong> Загрузите хотя бы один файл (немецкий или русский). 
                  Пользователи увидят документ на своем языке, если файл доступен.
                </p>
              </div>

              {/* German Name */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Название (Немецкий)
                </label>
                <input
                  type="text"
                  value={editingDocument ? editingDocument.name_de : newDocument.name_de}
                  onChange={(e) => {
                    if (editingDocument) {
                      setEditingDocument({ ...editingDocument, name_de: e.target.value });
                    } else {
                      setNewDocument({ ...newDocument, name_de: e.target.value });
                    }
                  }}
                  className="w-full px-3 py-2 bg-gray-700 text-white border border-gray-600 rounded-md focus:outline-none focus:border-blue-500"
                  placeholder="z.B. Vertrag herunterladen (PDF)"
                />
              </div>

              {/* Russian Name */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Название (Русский)
                </label>
                <input
                  type="text"
                  value={editingDocument ? editingDocument.name_ru : newDocument.name_ru}
                  onChange={(e) => {
                    if (editingDocument) {
                      setEditingDocument({ ...editingDocument, name_ru: e.target.value });
                    } else {
                      setNewDocument({ ...newDocument, name_ru: e.target.value });
                    }
                  }}
                  className="w-full px-3 py-2 bg-gray-700 text-white border border-gray-600 rounded-md focus:outline-none focus:border-blue-500"
                  placeholder="например: Скачать договор (PDF)"
                />
              </div>

              {/* German Description */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Описание (Немецкий)
                </label>
                <textarea
                  value={editingDocument ? editingDocument.description_de || '' : newDocument.description_de}
                  onChange={(e) => {
                    if (editingDocument) {
                      setEditingDocument({ ...editingDocument, description_de: e.target.value });
                    } else {
                      setNewDocument({ ...newDocument, description_de: e.target.value });
                    }
                  }}
                  rows={2}
                  className="w-full px-3 py-2 bg-gray-700 text-white border border-gray-600 rounded-md focus:outline-none focus:border-blue-500"
                  placeholder="Краткое описание документа на немецком языке"
                />
              </div>

              {/* Russian Description */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Описание (Русский)
                </label>
                <textarea
                  value={editingDocument ? editingDocument.description_ru || '' : newDocument.description_ru}
                  onChange={(e) => {
                    if (editingDocument) {
                      setEditingDocument({ ...editingDocument, description_ru: e.target.value });
                    } else {
                      setNewDocument({ ...newDocument, description_ru: e.target.value });
                    }
                  }}
                  rows={2}
                  className="w-full px-3 py-2 bg-gray-700 text-white border border-gray-600 rounded-md focus:outline-none focus:border-blue-500"
                  placeholder="Краткое описание документа на русском языке"
                />
              </div>

              {/* Sort Order */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Порядок сортировки
                </label>
                <input
                  type="number"
                  value={editingDocument ? editingDocument.sort_order || 0 : newDocument.sort_order}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || 0;
                    if (editingDocument) {
                      setEditingDocument({ ...editingDocument, sort_order: value });
                    } else {
                      setNewDocument({ ...newDocument, sort_order: value });
                    }
                  }}
                  className="w-full px-3 py-2 bg-gray-700 text-white border border-gray-600 rounded-md focus:outline-none focus:border-blue-500"
                  placeholder="0"
                />
              </div>

              {/* Active Status */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={editingDocument ? editingDocument.active : newDocument.active}
                  onChange={(e) => {
                    if (editingDocument) {
                      setEditingDocument({ ...editingDocument, active: e.target.checked });
                    } else {
                      setNewDocument({ ...newDocument, active: e.target.checked });
                    }
                  }}
                  className="mr-2"
                />
                <label className="text-sm text-gray-300">
                  Показывать документ на сайте
                </label>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-4 mt-6">
              <button
                onClick={() => {
                  setEditingDocument(null);
                  setShowAddForm(false);
                  resetForm();
                }}
                className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
              >
                Отмена
              </button>
              <button
                onClick={handleSaveDocument}
                disabled={saving || (!editingDocument && (!newDocument.name_de || !newDocument.name_ru || (!newDocument.file_url_de && !newDocument.file_url_ru)))}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-md transition-colors"
              >
                {saving ? 'Сохранение...' : 'Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsTab;